import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_COMPANY_BRANDING,
  LOGO_MAX_BYTES,
  type CompanyBranding as Branding,
  type CompanyBrandingResponse,
  type CompanyProfile,
  type InvoiceTemplate,
} from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import { BRAND_PALETTES, contrastRatio, readableTextOn } from './brand';
import { DocumentSheet, invalidateCompanyIdentity, SAMPLE_DOCUMENT } from './PrintDocument';
import Loading from './Loading';
import Hint from './Hint';

interface Props {
  companyId: string;
  /** الاسم والرقم الضريبى من «بيانات الشركة» — للمعاينة فقط، وتُحفظ هناك. */
  profile: CompanyProfile;
  canEdit: boolean;
  onSessionLost: () => void;
}

const TEMPLATES: { value: InvoiceTemplate; label: string; hint: string }[] = [
  {
    value: 'classic',
    label: 'كلاسيك',
    hint: 'ترويسة بيضاء وخط بلون البراند، رسمى ومقتصد فى الحبر.',
  },
  { value: 'modern', label: 'حديث', hint: 'شريط ترويسة بلون البراند كامل وجداول بزوايا ناعمة.' },
];

const ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml';

/**
 * هوية الشركة على الفاتورة: اللوجو والعنوان والتليفونات والألوان والقالب، بمعاينة
 * حية لفاتورة نموذجية. حفظها منفصل عن الإعدادات المحاسبية لأنها لا تمس أى ترحيل.
 */
export default function CompanyBranding({ companyId, profile, canEdit, onSessionLost }: Props) {
  const [loaded, setLoaded] = useState<CompanyBrandingResponse | null>(null);
  const [branding, setBranding] = useState<Branding>(DEFAULT_COMPANY_BRANDING);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const handleError = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      if (caught instanceof ProblemError) {
        const issues = caught.problem.issues ?? [];
        const status = caught.problem.status;
        setFailure(
          status === 413
            ? 'حجم اللوجو أكبر من ٥٠٠ ك.ب — صغّر الصورة ثم ارفعها.'
            : status === 415
              ? 'الملف ليس PNG أو JPEG أو WebP أو SVG آمنًا (' +
                (caught.problem.detail ?? '') +
                ').'
              : issues.length > 0
                ? issues.map((issue) => issue.path + ': ' + issue.message).join('، ')
                : (caught.problem.detail ?? caught.problem.title),
        );
        return;
      }
      setFailure(caught instanceof Error ? caught.message : String(caught));
    },
    [onSessionLost],
  );

  const showLogo = useCallback(
    (response: CompanyBrandingResponse) => {
      if (!response.logo) {
        setLogoUrl(null);
        return;
      }
      api
        .companyLogo(companyId)
        .then((blob) => setLogoUrl(blob ? URL.createObjectURL(blob) : null))
        .catch(handleError);
    },
    [companyId, handleError],
  );

  useEffect(() => {
    api
      .companyBranding(companyId)
      .then((data) => {
        setLoaded(data);
        setBranding(data.branding);
        showLogo(data);
      })
      .catch(handleError);
  }, [companyId, handleError, showLogo]);

  useEffect(
    () => () => {
      if (logoUrl) URL.revokeObjectURL(logoUrl);
    },
    [logoUrl],
  );

  function patch(value: Partial<Branding>): void {
    setBranding((current) => ({ ...current, ...value }));
    setNotice(null);
  }

  /** رفع اللوجو أو حذفه لا يمس الحقول: ما كتبه المالك ولم يحفظه يبقى كما هو. */
  function applied(
    result: CompanyBrandingResponse,
    message: string,
    { keepEdits = false } = {},
  ): void {
    setLoaded(result);
    if (!keepEdits) setBranding(result.branding);
    invalidateCompanyIdentity(companyId);
    setNotice(message);
  }

  async function save(): Promise<void> {
    setSaving(true);
    setFailure(null);
    try {
      applied(
        await api.updateCompanyBranding(companyId, {
          branding: { ...branding, phones: branding.phones.filter((phone) => phone.trim()) },
        }),
        'حُفظت هوية الشركة — تظهر فى كل فاتورة تُطبع بعد الآن.',
      );
    } catch (caught) {
      handleError(caught);
    } finally {
      setSaving(false);
    }
  }

  async function upload(file: File): Promise<void> {
    setFailure(null);
    if (file.size > LOGO_MAX_BYTES) {
      setFailure('حجم اللوجو أكبر من ٥٠٠ ك.ب — صغّر الصورة ثم ارفعها.');
      return;
    }
    setUploading(true);
    try {
      const result = await api.uploadCompanyLogo(companyId, file);
      applied(result, 'رُفع اللوجو.', { keepEdits: true });
      showLogo(result);
    } catch (caught) {
      handleError(caught);
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function removeLogo(): Promise<void> {
    setFailure(null);
    setUploading(true);
    try {
      const result = await api.deleteCompanyLogo(companyId);
      applied(result, 'حُذف اللوجو.', { keepEdits: true });
      setLogoUrl(null);
    } catch (caught) {
      handleError(caught);
    } finally {
      setUploading(false);
    }
  }

  if (!loaded) {
    return (
      <div className="panel">{failure ? <p className="error">{failure}</p> : <Loading />}</div>
    );
  }

  const disabled = !canEdit || saving;
  const { primary, secondary } = branding.colors;
  const ink = readableTextOn(primary);
  const phones = [...branding.phones, '', '', ''].slice(0, 3);
  const text = (value: string) => (value.trim() === '' ? null : value);

  return (
    // داخل نموذج الإعدادات: div لا form (لا نماذج متداخلة)، وEnter فى حقولها لا يحفظ الإعدادات
    <div
      className="panel branding"
      onKeyDown={(event) => {
        if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
          event.preventDefault();
        }
      }}
    >
      <h2>هوية الشركة على الفاتورة</h2>
      <p className="sub">
        اللوجو والعنوان والتليفونات والألوان تظهر فى ترويسة كل فاتورة وإشعار وإيصال يُطبع.
      </p>

      <div className="branding-grid">
        <div className="branding-fields">
          <fieldset className="option-group" disabled={!canEdit || uploading}>
            <legend>اللوجو</legend>
            <div className="logo-row">
              <div className="logo-box">
                {logoUrl ? <img src={logoUrl} alt="لوجو الشركة" /> : <span>بلا لوجو</span>}
              </div>
              <div>
                <input
                  ref={fileInput}
                  type="file"
                  accept={ACCEPT}
                  hidden
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) void upload(file);
                  }}
                />
                <div className="form-actions">
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => fileInput.current?.click()}
                  >
                    {uploading ? 'جارى الرفع…' : loaded.logo ? 'تغيير اللوجو' : 'رفع لوجو'}
                  </button>
                  {loaded.logo && (
                    <button type="button" className="link" onClick={() => void removeLogo()}>
                      حذف
                    </button>
                  )}
                </div>
                <span className="label">
                  PNG أو JPEG أو WebP أو SVG حتى ٥٠٠ ك.ب. الأوضح خلفية شفافة.
                </span>
              </div>
            </div>
          </fieldset>

          <fieldset className="option-group" disabled={disabled}>
            <legend>العنوان والتواصل</legend>
            <div className="form-row">
              <label className="grow">
                العنوان (سطر ١)
                <input
                  value={branding.address.line1 ?? ''}
                  maxLength={200}
                  placeholder="١٢ شارع التحرير"
                  onChange={(event) =>
                    patch({ address: { ...branding.address, line1: text(event.target.value) } })
                  }
                />
                <Hint>يظهر أعلى الفواتير والمستندات المطبوعة.</Hint>
              </label>
              <label className="grow">
                العنوان (سطر ٢)
                <input
                  value={branding.address.line2 ?? ''}
                  maxLength={200}
                  placeholder="الدور الثالث — مكتب ٥"
                  onChange={(event) =>
                    patch({ address: { ...branding.address, line2: text(event.target.value) } })
                  }
                />
              </label>
            </div>
            <div className="form-row">
              <label className="grow">
                المدينة / الحى
                <input
                  value={branding.address.city ?? ''}
                  maxLength={100}
                  onChange={(event) =>
                    patch({ address: { ...branding.address, city: text(event.target.value) } })
                  }
                />
              </label>
              <label className="grow">
                المحافظة
                <input
                  value={branding.address.governorate ?? ''}
                  maxLength={100}
                  onChange={(event) =>
                    patch({
                      address: { ...branding.address, governorate: text(event.target.value) },
                    })
                  }
                />
              </label>
            </div>
            <div className="form-row">
              {phones.map((phone, index) => (
                <label key={index} className="grow">
                  تليفون {index + 1}
                  <input
                    dir="ltr"
                    inputMode="tel"
                    value={phone}
                    maxLength={24}
                    onChange={(event) => {
                      const next = [...phones];
                      next[index] = event.target.value;
                      patch({ phones: next.filter((value) => value !== '') });
                    }}
                  />
                </label>
              ))}
            </div>
            <div className="form-row">
              <label className="grow">
                البريد الإلكترونى
                <input
                  dir="ltr"
                  type="email"
                  value={branding.email ?? ''}
                  maxLength={200}
                  onChange={(event) => patch({ email: text(event.target.value) })}
                />
              </label>
              <label className="grow">
                الموقع
                <input
                  dir="ltr"
                  value={branding.website ?? ''}
                  maxLength={200}
                  placeholder="www.example.com"
                  onChange={(event) => patch({ website: text(event.target.value) })}
                />
              </label>
            </div>
            <label className="grow">
              تذييل الفاتورة (اختيارى)
              <textarea
                rows={3}
                value={branding.invoiceFooter ?? ''}
                maxLength={1000}
                placeholder="شروط الدفع، أو بيانات الحساب البنكى للتحويل"
                onChange={(event) => patch({ invoiceFooter: text(event.target.value) })}
              />
              <Hint>سطر أسفل كل فاتورة، مثل: «البضاعة المباعة تُستبدل خلال ١٤ يومًا».</Hint>
            </label>
          </fieldset>

          <fieldset className="option-group" disabled={disabled}>
            <legend>الألوان</legend>
            <div className="palettes">
              {BRAND_PALETTES.map((palette) => (
                <button
                  key={palette.name}
                  type="button"
                  className={
                    'palette' +
                    (palette.primary === primary && palette.secondary === secondary
                      ? ' active'
                      : '')
                  }
                  onClick={() =>
                    patch({ colors: { primary: palette.primary, secondary: palette.secondary } })
                  }
                >
                  <span className="palette-swatch" style={{ background: palette.primary }} />
                  <span className="palette-swatch" style={{ background: palette.secondary }} />
                  {palette.name}
                </button>
              ))}
            </div>
            <div className="form-row">
              <label className="color-field">
                اللون الأساسى
                <span>
                  <input
                    type="color"
                    value={primary}
                    onChange={(event) =>
                      patch({ colors: { ...branding.colors, primary: event.target.value } })
                    }
                  />
                  <code dir="ltr">{primary}</code>
                </span>
                <Hint>لون ترويسة الفاتورة والجداول المطبوعة.</Hint>
              </label>
              <label className="color-field">
                اللون الثانوى
                <span>
                  <input
                    type="color"
                    value={secondary}
                    onChange={(event) =>
                      patch({ colors: { ...branding.colors, secondary: event.target.value } })
                    }
                  />
                  <code dir="ltr">{secondary}</code>
                </span>
                <Hint>لون الخطوط الفاصلة والإبراز فى المطبوعات.</Hint>
              </label>
              <span className="contrast-chip" style={{ background: primary, color: ink }}>
                النص فوق الأساسى {ink === '#ffffff' ? 'أبيض' : 'أسود'} • تباين{' '}
                {contrastRatio(primary, ink).toFixed(1)}:1
              </span>
            </div>
          </fieldset>

          <fieldset className="option-group" disabled={disabled}>
            <legend>قالب الفاتورة</legend>
            {TEMPLATES.map((template) => (
              <label key={template.value} className="option">
                <input
                  type="radio"
                  checked={branding.invoiceTemplate === template.value}
                  onChange={() => patch({ invoiceTemplate: template.value })}
                />
                <span>
                  <strong>{template.label}</strong>
                  <span className="label">{template.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
        </div>

        <div className="branding-preview" aria-label="معاينة الفاتورة">
          <div className="preview-scale">
            <DocumentSheet doc={SAMPLE_DOCUMENT} identity={{ profile, branding, logoUrl }} />
          </div>
        </div>
      </div>

      {failure && <p className="error">{failure}</p>}
      {notice && <p className="sub">{notice}</p>}
      {canEdit && (
        <div className="form-actions">
          <button type="button" className="primary" disabled={saving} onClick={() => void save()}>
            {saving ? 'جارى الحفظ…' : 'حفظ الهوية'}
          </button>
          <span className="label">
            المعاينة ببيانات نموذجية — الفاتورة الفعلية من شاشة الفواتير.
          </span>
        </div>
      )}
    </div>
  );
}
